import { useLazyQuery } from '@apollo/client';
import TableError from 'components/TableError';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Badge, Card, CardBody, Col, Container, Input, Row, Table } from 'reactstrap';
import { ACTIVE_AND_DELETED_ARRAY, SORT_VALUES_DATE } from 'utils/constants';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import Loader from '../../../components/Loader';
import Paginate from '../../../components/Paginate';
import { useRouter } from '../../../hooks/useRouter';
import {
  checkIfValueExist,
  createPostDetailsLink,
  printDateTime,
  printSerialNumbers,
  urlSlugify,
} from '../../../utils/helper';
import { COMMUNITY_REPORTED_POSTS_SEARCH } from '../graphql/community.query';
import Select from 'react-select';

const CommunityReportedreportedPosts = () => {
  const router = useRouter();
  const history = useHistory();
  toast.configure();

  const [reportedPosts, setReportedreportedPosts] = useState([]);
  const [reportedPostsPagination, setReportedPostsPagination] = useState({});

  const defaultFilters = {
    active: { value: null, label: 'All' },
    deleted: { value: null, label: 'All' },
    name: '',
  };

  const [filters, setFilters] = useState(defaultFilters);

  const [getReportedreportedPostsRequest, { loading: getReortedreportedPostsLoading }] = useLazyQuery(
    COMMUNITY_REPORTED_POSTS_SEARCH,
    {
      onCompleted: (data) => {
        if (!isEmpty(data?.admin_community_post_report_search)) {
          setReportedreportedPosts(data?.admin_community_post_report_search?.results);
          setReportedPostsPagination(data?.admin_community_post_report_search?.pagination);
        }
      },
      onError: () => {
        toast.error('Something went wrong!');
      },
    }
  );

  const setPageInUrl = (page) => {
    history.push({
      pathname: router.pathname,
      search: `?${new URLSearchParams({ page }).toString()}`,
    });
  };

  const handleChangePage = async (page) => {
    setPageInUrl(page);
    await getReportedreportedPostsRequest({
      variables: {
        searchDTO: {
          page,
          size: 20,
          ...filters,
          active: checkIfValueExist(filters?.active?.value),
          deleted: checkIfValueExist(filters?.deleted?.value),
          name: urlSlugify(filters?.name?.trim()),
          ...SORT_VALUES_DATE,
        },
      },
    });
  };

  const applyFilters = () => {
    handleChangePage(1);
  };

  const clearFilters = async () => {
    setFilters({ ...defaultFilters, name: '' });
    await getReportedreportedPostsRequest({
      variables: {
        searchDTO: {
          page: 1,
          size: 20,
          ...SORT_VALUES_DATE,
        },
      },
    });
  };

  useEffect(() => {
    handleChangePage(parseInt(router?.query?.page, 10) || 1);
  }, []);

  return (
    <>
      <div className="page-content">
        <Loader isActive={getReortedreportedPostsLoading} />

        <Container fluid>
          <Breadcrumbs title="Community" breadcrumbItem="Community Reported Posts list" />

          <Card>
            <CardBody>
              <div className="mt-4">
                <div className="row">
                  <div className="col-lg-2 mb-4">
                    <label className="form-label">Active</label>
                    <Select
                      options={ACTIVE_AND_DELETED_ARRAY}
                      value={filters.active}
                      onChange={(value) => setFilters({ ...filters, active: value })}
                    />
                  </div>
                  <div className="col-lg-2 mb-4">
                    <label className="form-label">Deleted</label>
                    <Select
                      options={ACTIVE_AND_DELETED_ARRAY}
                      value={filters.deleted}
                      onChange={(value) => setFilters({ ...filters, deleted: value })}
                    />
                  </div>

                  <div className="col-lg-3 mb-4">
                    <label htmlFor="name" className="form-label">
                      Name
                    </label>
                    <Input
                      type="text"
                      name="name"
                      placeholder="Name..."
                      value={filters.name}
                      onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-2">
                <a className="btn btn-primary me-4" onClick={applyFilters}>
                  Apply Filters
                </a>
                <a className="" onClick={clearFilters}>
                  Clear
                </a>
              </div>
            </CardBody>
          </Card>

          <Row className="mb-2 align-items-center">
            <Col sm="4">
              <div className="search-box me-2 mb-2 d-inline-block">
                <div className="position-relative font-bold">
                  {reportedPostsPagination?.totalCount} Reported Posts found
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg="12">
              <div className="table-responsive" style={{ minHeight: 300 }}>
                <Table className="project-list-table   table-centered table-borderless">
                  <thead>
                    <tr className="table-warning">
                      <th scope="col">S.No.</th>
                      <th scope="col">Post Title</th>
                      <th scope="col">Reason</th>
                      <th scope="col">Comments</th>
                      <th scope="col">CreatedBy</th>
                      <th scope="col">Created Date</th>
                      <th scope="col">Updated Date</th>
                      <th scope="col">Active</th>
                      <th scope="col">Deleted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!isEmpty(reportedPosts) ? (
                      reportedPosts?.map((elem, index) => {
                        return (
                          <tr key={`post_${elem?.id}`} className={!elem?.active ? 'bg-danger' : ''}>
                            <td>{printSerialNumbers(reportedPostsPagination)[index]}</td>
                            <td>
                              <a target="_blank" rel="noreferrer" href={createPostDetailsLink(elem)}>
                                {elem?.post?.title}
                              </a>
                            </td>
                            <td>{elem?.reason}</td>
                            <td>{elem?.comments}</td>
                            <td>{elem?.createdBy?.displayName}</td>
                            <td>{printDateTime(elem?.createdDate)}</td>
                            <td>{printDateTime(elem?.updatedDate)}</td>
                            <td>
                              <Badge className={elem?.active ? 'bg-success' : 'bg-danger'}>
                                {elem?.active ? 'Yes' : 'No'}
                              </Badge>
                            </td>
                            <td>
                              <Badge className={elem?.deleted ? 'bg-success' : 'bg-danger'}>
                                {elem?.deleted ? 'Yes' : 'No'}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <TableError />
                    )}
                  </tbody>
                </Table>
              </div>
            </Col>
          </Row>

          <Paginate pageInfo={reportedPostsPagination} onChangePage={handleChangePage} />
        </Container>
      </div>
    </>
  );
};

export default CommunityReportedreportedPosts;
